<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <h2 class="text-xl font-bold">Find DLL Missing</h2>
      <button class="btn btn-primary btn-sm" @click="open_scan_dialog">
        <i class="fa-solid fa-magnifying-glass"></i> Find DLL missing
      </button>
    </div>

    <!-- Search only filters the currently loaded events page. -->
    <div class="grow">
      <div class="mb-2 flex items-center gap-2">
        <input
          v-model="keyword"
          type="search"
          class="input input-bordered input-sm grow"
          placeholder="Search this page (e.g. NAME NOT FOUND, dll name)..."
        />
      </div>
      <Loading v-if="loading_events" />
      <div v-else-if="visible_events.length > 0" class="overflow-x-auto">
        <table class="table-pin-rows table-md mb-4 table">
          <thead>
            <tr>
              <th></th>
              <th>Process</th>
              <th>PID</th>
              <th>Operation</th>
              <th>Result</th>
              <th>Path</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="bg-base-200">
            <tr
              v-for="(evt, idx) in page_events"
              :key="evt.id"
              class="cursor-pointer hover:bg-white/40"
              @click="select_event(evt)"
            >
              <th>{{ idx + (page - 1) * size }}</th>
              <td>{{ evt.process_name }}</td>
              <td>{{ evt.pid }}</td>
              <td>{{ evt.operation }}</td>
              <td class="text-xs">{{ evt.result }}</td>
              <td class="max-w-md truncate" :title="evt.path">{{ evt.path }}</td>
              <td @click.stop>
                <button class="btn btn-ghost btn-xs" @click="open_hijack_dialog(evt, 'dll-hijack')">
                  DLL hijack
                </button>
                <button class="btn btn-ghost btn-xs" @click="open_hijack_dialog(evt, 'com-hijack')">
                  COM hijack
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <Pagination page_query_key="events_page" :show_per_page="false" :total_pages="total_pages" />
        <button class="btn btn-sm" @click="load_events">Làm mới</button>
      </div>
      <NotFound v-else class="py-10 text-center text-base-content/50">
        Không có sự kiện DLL missing. Bắt đầu quét trước.
      </NotFound>
    </div>

    <!-- ponytail: hijack actions are tracked per-agent regardless of scan; list them so the user can roll back a successful exploit (backend 8045ed2). Reuses the shared status/watcher pattern from the hijack flow above. -->
    <section class="rounded border border-base-300 p-4">
      <div class="mb-3 flex items-center justify-between">
        <h2 class="text-lg font-bold">Khai thác DLL/Hijack</h2>
        <div class="flex gap-2">
          <button class="btn btn-outline btn-sm" @click="open_hijack_dialog_standalone('dll-hijack')">Tạo DLL Hijack</button>
          <button class="btn btn-outline btn-sm" @click="open_hijack_dialog_standalone('com-hijack')">Tạo COM Hijack</button>
          <button class="btn btn-outline btn-sm" :disabled="action_store.loading.get_action_history" @click="load_hijacks">
          <i v-if="!action_store.loading.get_action_history" class="fa-solid fa-rotate-right"></i> Làm mới
        </button>
        </div>
      </div>

      <Loading v-if="loading_hijacks" />
      <div v-else-if="visible_hijacks.length > 0" class="overflow-x-auto">
        <table class="table-pin-rows table-md mb-4 table">
          <thead>
            <tr>
              <th>Loại</th>
              <th>Thời gian</th>
              <th>Thư mục lưu</th>
              <th>Tên DLL / CLSID</th>
              <th>Kết quả</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="bg-base-200">
            <tr v-for="h in visible_hijacks" :key="h.id">
              <td><span class="badge badge-ghost badge-sm">{{ h.type }}</span></td>
              <td>{{ format_time(h.created) }}</td>
              <td class="max-w-xs truncate" :title="h.save_dir">{{ h.save_dir || "—" }}</td>
              <td class="max-w-xs truncate" :title="h.clsid || h.dll_name_on_victim">{{ h.clsid || h.dll_name_on_victim || "—" }}</td>
              <td class="text-xs">
                <span v-if="h.rollback_status === 'sent'" class="badge badge-info badge-sm">Đã gửi · chưa có kết quả</span>
                <span v-else-if="h.rollback_status === 'pending'" class="badge badge-warning badge-sm">Đang chờ agent</span>
                <span v-else-if="h.rollback_status === 'no_result'" class="badge badge-warning badge-sm">Chưa nhận kết quả rollback · có thể thử lại</span>
                <span v-else-if="h.rollback_status === 'complete'" class="badge badge-success badge-sm">Rollback hoàn tất</span>
                <span v-else-if="h.rollback_status === 'failed'" class="badge badge-error badge-sm">Rollback thất bại · có thể thử lại</span>
                <span v-else-if="is_complete(h)" class="badge badge-success badge-sm">Thành công</span>
                <span v-else-if="h.status === 'error'" class="badge badge-error badge-sm">Thất bại</span>
                <span v-else class="badge badge-warning badge-sm">{{ h.status }}</span>
              </td>
              <td @click.stop>
                <button
                  :disabled="!is_complete(h) || h.rollback_status === 'pending' || h.rollback_status === 'sent' || h.rollback_status === 'complete'"
                  title="Rollback hijack trên nạn nhân"
                  class="btn btn-outline btn-xs"
                  @click="do_rollback(h)"
                >
                  Rollback
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <Pagination page_query_key="hijacks_page" :show_per_page="false" :total_pages="hijacks_total_pages" />
      </div>
      <NotFound v-else class="py-10 text-center text-base-content/50">
        Không có khai thác nào. Bấm Hijack trên một sự kiện DLL missing để bắt đầu.
      </NotFound>

      <!-- ponytail: rollback confirmation — deletion on the victim is irreversible, so gate it behind an explicit prompt. -->
      <dialog ref="rollback_dialog" id="find_dml_rollback_modal" class="modal">
        <div class="modal-box w-96 max-w-none">
          <h3 class="mb-4 text-lg font-bold">Xác nhận Rollback</h3>
          <p v-if="pending_hijack" class="text-sm text-base-content/70">
            Rollback {{ pending_hijack.clsid ? `COM (CLSID ${pending_hijack.clsid})` : `DLL ${pending_hijack.dll_name_on_victim}` }} tại {{ pending_hijack.save_dir }} trên nạn nhân?
          </p>
          <FindDmlModulePicker
            v-model="rollback_module_version_id"
            :agent-id="agentId"
            :type="pending_hijack?.clsid ? 'find-dml-com-hijack-rollback' : 'find-dml-dll-hijack-rollback'"
            :auto-select-latest="true"
            accept=".dll,.bin"
            :label="pending_hijack?.clsid ? 'Module Rollback COM' : 'Module Rollback DLL'"
          />
          <div class="modal-action">
            <form method="dialog" class="flex gap-2">
              <button class="btn btn-ghost btn-sm" value="cancel">Hủy</button>
              <button class="btn btn-primary btn-sm" :disabled="action_store.loading.create_action" @click="confirm_rollback">
                <span v-if="action_store.loading.create_action"><Loading /></span>
                <span v-else>Xác nhận</span>
              </button>
            </form>
          </div>
        </div>
      </dialog>
    </section>

      <!-- Scan start modal (procmon tool upload + runtime) -->
      <dialog ref="scan_dialog" id="find_dml_scan_modal" class="modal">
        <div class="modal-box w-112 max-w-none">
          <h3 class="mb-4 text-lg font-bold">Scan DLL Missing</h3>
          <label class="block text-left text-sm">Runtime (giây)</label>
        <input
          v-model.number="runtime"
          type="number"
          min="1"
          class="input input-bordered input-sm w-full mb-4"
        />
        <FindDmlModulePicker
          v-model="scan_module_version_id"
          :agent-id="agentId"
          type="find-dll-missing-scan"
          accept=".exe"
          label="Công cụ scan"
        />
        <div class="modal-action">
          <form method="dialog" class="flex gap-2">
            <button class="btn" value="cancel">Hủy</button>
            <button
              class="btn btn-primary min-w-28"
              :disabled="!can_submit_scan || action_store.loading.create_action"
              @click="handle_start_scan"
            >
              <span v-if="action_store.loading.create_action"><Loading /></span>
              <span v-else>Bắt đầu quét</span>
            </button>
          </form>
        </div>
      </div>
    </dialog>

    <!-- Hijack modal (select DLL file, save dir, dll name) -->
    <dialog ref="hijack_dialog" id="find_dml_hijack_modal" class="modal">
      <div class="modal-box w-128 max-w-none">
        <h3 class="mb-4 text-lg font-bold">{{ hijack_type === 'com-hijack' ? 'Khai thác COM Hijack' : 'Khai thác DLL Hijack' }}</h3>
        <label v-if="!hijack_event" class="mb-1 block text-left text-sm">Sự kiện nguồn (không bắt buộc)</label>
        <select v-if="!hijack_event" v-model="standalone_event_id" class="select select-bordered select-sm mb-4 w-full">
          <option value="">Không chọn sự kiện nguồn</option>
          <option v-for="evt in events" :key="evt.id" :value="evt.id">{{ evt.process_name }} ({{ evt.pid }}) — {{ evt.path }}</option>
        </select>
        <div v-if="hijack_event" class="mb-4 rounded bg-base-200/60 p-2 text-xs">
          <div><span class="font-medium">{{ hijack_event.process_name }}</span>
            ({{ hijack_event.pid }}) — {{ hijack_event.operation }}: {{ hijack_event.result }}</div>
          <div class="truncate" :title="hijack_event.path">{{ hijack_event.path }}</div>
        </div>

        <label v-if="hijack_type === 'com-hijack'" class="block text-left text-sm">CLSID</label>
        <input v-if="hijack_type === 'com-hijack'" v-model="clsid" class="input input-bordered input-sm w-full mb-4" placeholder="{...}" />
        <FindDmlModulePicker
          v-model="hijack_module_version_id"
          :agent-id="agentId"
          :type="hijack_type === 'com-hijack' ? 'find-dml-com-hijack' : 'find-dml-hijack'"
          accept=".dll"
          label="Module DLL"
        />

        <label class="block text-left text-sm">Thư mục lưu DLL trên nạn nhân</label>
        <input v-model="save_dir" class="input input-bordered input-sm w-full mb-4" />

        <div class="modal-action">
          <form method="dialog" class="flex gap-2">
            <button class="btn" value="cancel">Hủy</button>
            <button
              class="btn btn-primary min-w-28"
              :disabled="!can_submit_hijack || action_store.loading.create_action"
              @click="handle_start_hijack"
            >
              <span v-if="action_store.loading.create_action"><Loading /></span>
              <span v-else>Khai thác</span>
            </button>
          </form>
        </div>
      </div>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import Loading from "@/components/LoadingTemplate.vue";
import NotFound from "@/components/NotFound.vue";
import Pagination from "@/components/PaginationTemplate.vue";
import FindDmlModulePicker from "./FindDmlModulePicker.vue";
import { toast } from "vue3-toastify";
import { AxiosError as AxiosErrorClass } from "axios";
import { use_action } from "../ExplorerV2/useAction";
import { resolveHijackParentEventId } from "./hijackParentId";
import { defaultSaveDir } from "./eventPath";
import {
  createFindDMLScan,
  createFindDMLHijack,
  listFindDMLEvents,
  listFindDMLHijacks,
  triggerFindDmlRollback,
  type FindDMLEvent,
  type FindDMLHijackAction,
} from "@/api/findDml";

const action_store = use_action();
const route = useRoute();
const router = useRouter();

const agentId = computed(() => route.params.client_id as string);

// --- scan modal state ---
const scan_dialog = ref<HTMLDialogElement | null>(null);
const runtime = ref(30);
const scan_module_version_id = ref("");
const open_scan_dialog = () => {
  if (scan_dialog.value) scan_dialog.value.showModal();
};
const can_submit_scan = computed(() => !!scan_module_version_id.value && runtime.value > 0);

// --- events store: backend-paginated rows for the active scan; keyword filters this page. ---
const keyword = ref("");
const events = ref<FindDMLEvent[]>([]);
const events_total = ref(0);
// Each paginated list is driven by its own URL query key.
const query_page = (key: string): number => {
  const raw = route.query[key];
  const value = Number(Array.isArray(raw) ? raw[0] : raw ?? 1);
  return Number.isInteger(value) && value > 0 ? value : 1;
};
const page = computed(() => query_page("events_page"));
const hpage = computed(() => query_page("hijacks_page"));
const set_query_page = (key: string, value: number) =>
  router.replace({ query: { ...route.query, [key]: String(value) } });
const size = 20;
const loading_events = ref(false);
let active_scan_id = "";

const visible_events = computed(() => events.value);

const total_pages = computed(() =>
  Math.max(1, Math.ceil(events_total.value / size)),
);
const page_events = computed(() => {
  return visible_events.value;
});

// Backend paginates events; search is intentionally limited to the loaded page.
const load_events = async () => {
  loading_events.value = true;
  try {
    const res = await listFindDMLEvents(agentId.value, active_scan_id || undefined, page.value, size, keyword.value.trim());
    events.value = res.data ?? [];
    events_total.value = res.total_filter;
    const last_page = Math.max(1, Math.ceil(res.total_filter / size));
    if (page.value > last_page) {
      await set_query_page("events_page", last_page);
      return;
    }
  } catch (err) {
    if (err instanceof AxiosErrorClass && err.response?.data?.message) {
      toast.error(err.response.data.message);
    } else {
      toast.error("Đã xảy ra lỗi khi lấy sự kiện.");
    }
  } finally {
    loading_events.value = false;
  }
};

// --- hijack modal state ---
const hijack_dialog = ref<HTMLDialogElement | null>(null);
const hijack_event = ref<FindDMLEvent | null>(null);
const standalone_event_id = ref("");
const hijack_module_version_id = ref("");
const hijack_type = ref<"dll-hijack" | "com-hijack">("dll-hijack");

const clsid = ref("");
const save_dir = ref("");

const select_event = (e: FindDMLEvent) => {
  hijack_event.value = e;
  save_dir.value = defaultSaveDir(e.path);
};

const open_hijack_dialog = (e?: FindDMLEvent, type: "dll-hijack" | "com-hijack" = "dll-hijack") => {
  hijack_type.value = type;
  clsid.value = "";
  standalone_event_id.value = "";

  if (e) select_event(e); else { hijack_event.value = null; save_dir.value = ""; }
  hijack_module_version_id.value = "";
  if (hijack_dialog.value) hijack_dialog.value.showModal();
};

const open_hijack_dialog_standalone = (type: "dll-hijack" | "com-hijack") => open_hijack_dialog(undefined, type);

const can_submit_hijack = computed(() => {
  const hasEvent = !!(hijack_event.value?.id || standalone_event_id.value);
  return !!hijack_module_version_id.value
    && (hijack_type.value !== "com-hijack" || !!clsid.value.trim())
    && (hijack_type.value !== "dll-hijack" || hasEvent || !!save_dir.value.trim());
});

const handle_start_scan = async () => {
  try {
    if (!agentId.value) {
      toast.error("Thiếu ID workstation.");
      return;
    }
    const action_id = await createFindDMLScan({
      agentId: agentId.value,
      runtime: runtime.value,
      moduleVersionId: scan_module_version_id.value,
    });
    active_scan_id = action_id;
    if (scan_dialog.value) scan_dialog.value.close();
    await load_events();
    toast.success("Đã gửi yêu cầu quét DLL missing.");
  } catch (err) {
    if (err instanceof AxiosErrorClass && err.response?.data?.message) {
      toast.error(err.response.data.message);
    } else {
      toast.error("Đã xảy ra lỗi khi bắt đầu quét.");
    }
  }
};

const handle_start_hijack = async () => {
  try {
    if (!agentId.value) return;
    const parentId = resolveHijackParentEventId("", hijack_event.value?.id || standalone_event_id.value);
    const action_id = await createFindDMLHijack({
      agentId: agentId.value,
      type: hijack_type.value,
      parentEventId: parentId || undefined,
      clsid: hijack_type.value === "com-hijack" ? clsid.value.trim() : undefined,
      saveDir: save_dir.value || undefined,
      dllNameOnVictim: "",
      moduleVersionId: hijack_module_version_id.value,
    });
    if (hijack_dialog.value) hijack_dialog.value.close();
    // ponytail: reuse the shared action watcher so status/result flows through.
    action_store.watch_for_action(action_id, {
      on_complete: () => { toast.success("Khai thác DLL thành công."); load_hijacks(); },
      on_fail: () => { toast.error("Khai thác DLL thất bại."); load_hijacks(); },
    });
    // ponytail: watch_for_action polls in the background and never resolves, so
    // refresh the hijacks list here instead of relying on a completion callback.
    await load_hijacks();
  } catch (err) {
    if (err instanceof AxiosErrorClass && err.response?.data?.message) {
      toast.error(err.response.data.message);
    } else {
      toast.error("Đã xảy ra lỗi khi khai thác.");
    }
  }
};

// --- hijacks list + rollback state (backend 8045ed2) ---
const hijacks = ref<FindDMLHijackAction[]>([]);
const loading_hijacks = ref(false);
const hsize = 20;
const hijacks_total = ref(0);
const pending_hijack = ref<FindDMLHijackAction | null>(null);
const rollback_module_version_id = ref("");
const rollback_dialog = ref<HTMLDialogElement | null>(null);

const visible_hijacks = computed(() => hijacks.value);
const hijacks_total_pages = computed(() => Math.max(1, Math.ceil(hijacks_total.value / hsize)));

const format_time = (ms: number): string => {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString("vi-VN");
  } catch {
    return String(ms);
  }
};
const is_complete = (h: FindDMLHijackAction): boolean => h.status === "complete";

const load_hijacks = async () => {
  if (!agentId.value) return;
  loading_hijacks.value = true;
  try {
    const res = await listFindDMLHijacks(agentId.value, undefined, undefined, hpage.value, hsize);
    hijacks.value = res.data ?? [];
    hijacks_total.value = res.total_filter;
    const last_page = Math.max(1, Math.ceil(res.total_filter / hsize));
    if (hpage.value > last_page) {
      await set_query_page("hijacks_page", last_page);
      return;
    }
  } catch (err) {
    if (err instanceof AxiosErrorClass && err.response?.data?.message) {
      toast.error(err.response.data.message);
    } else {
      toast.error("Đã xảy ra lỗi khi lấy danh sách khai thác.");
    }
  } finally {
    loading_hijacks.value = false;
  }
};

const do_rollback = (h: FindDMLHijackAction) => {
  pending_hijack.value = h;
  if (rollback_dialog.value) rollback_dialog.value.showModal();
};

const confirm_rollback = async () => {
  const target = pending_hijack.value;
  if (!target || !agentId.value) return;
  try {
    const action_id = await triggerFindDmlRollback({ agentId: agentId.value, hijackActionId: target.id, moduleVersionId: rollback_module_version_id.value || undefined });
    if (rollback_dialog.value) rollback_dialog.value.close();
    pending_hijack.value = null;
    toast.info(`Rollback được xếp hàng (action ${action_id}); chưa có kết quả từ agent.`);
    await load_hijacks();
  } catch (err) {
    if (err instanceof AxiosErrorClass && err.response?.data?.message) {
      toast.error(err.response.data.message);
    } else {
      toast.error("Đã xảy ra lỗi khi rollback.");
    }
  }
};

watch(
  keyword,
  () => {
    if (page.value !== 1) void set_query_page("events_page", 1);
    else void load_events();
  },
);
watch(
  () => route.query.events_page,
  (value, previous) => {
    const next = query_page("events_page");
    if (String(value ?? "1") !== String(next)) {
      void set_query_page("events_page", next);
    } else if (value !== previous) {
      void load_events();
    }
  },
);
watch(
  () => route.query.hijacks_page,
  (value, previous) => {
    const next = query_page("hijacks_page");
    if (String(value ?? "1") !== String(next)) {
      void set_query_page("hijacks_page", next);
    } else if (value !== previous) {
      void load_hijacks();
    }
  },
);

onMounted(() => {
  load_events();
  load_hijacks();
});
</script>
